import { Vector3 } from 'three';
import { BALANCE } from '../config/balance';

export type SoundName =
  | 'shot'
  | 'hit'
  | 'explosion'
  | 'footstep'
  | 'magnet'
  | 'jump'
  | 'land'
  | 'ui'
  | 'droneHum'
  | 'droneShot'
  | 'alarm';

interface PositionalSource {
  panner: PannerNode;
  gain: GainNode;
  oscillators: OscillatorNode[];
}

/**
 * Procedural audio. Every sound is synthesised with the Web Audio API, which
 * keeps the project free of third-party audio licences and makes the spatial
 * cues (which locate threats on other surfaces) easy to author.
 */
export class AudioManager {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private ambienceBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private readonly sources = new Map<string, PositionalSource>();
  private ambience: OscillatorNode | null = null;
  private ambienceGain: GainNode | null = null;
  private musicNodes: OscillatorNode[] = [];
  private musicGain: GainNode | null = null;
  private readonly counts: Record<string, number> = {};

  private volumes: {
    master: number;
    sfx: number;
    ambience: number;
    music: number;
    muted: boolean;
  } = {
    master: BALANCE.audio.master,
    sfx: BALANCE.audio.sfx,
    ambience: BALANCE.audio.ambience,
    music: BALANCE.audio.music,
    muted: false,
  };

  private readonly listenerPosition = new Vector3();
  private readonly listenerForward = new Vector3();
  private readonly listenerUp = new Vector3();

  get isReady(): boolean {
    return this.context !== null;
  }

  /** Must be called from a user gesture (browser autoplay policy). */
  async resume(): Promise<void> {
    if (!this.context) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.context = new Ctor();
      this.master = this.context.createGain();
      this.master.gain.value = this.volumes.muted ? 0 : this.volumes.master;

      // A soft limiter keeps additive layers from clipping.
      const limiter = this.context.createDynamicsCompressor();
      limiter.threshold.value = -12;
      limiter.knee.value = 6;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.25;
      this.master.connect(limiter);
      limiter.connect(this.context.destination);

      this.sfxBus = this.context.createGain();
      this.sfxBus.gain.value = this.volumes.sfx;
      this.sfxBus.connect(this.master);

      this.ambienceBus = this.context.createGain();
      this.ambienceBus.gain.value = this.volumes.ambience;
      this.ambienceBus.connect(this.master);

      this.musicBus = this.context.createGain();
      this.musicBus.gain.value = this.volumes.music;
      this.musicBus.connect(this.master);

      this.startMusic();
    }
    if (this.context.state === 'suspended') await this.context.resume();
  }

  setVolumes(master: number, sfx: number, ambience: number, music?: number, muted?: boolean): void {
    this.volumes = {
      master,
      sfx,
      ambience,
      music: music ?? this.volumes.music,
      muted: muted ?? this.volumes.muted,
    };
    if (this.master) this.master.gain.value = this.volumes.muted ? 0 : master;
    if (this.sfxBus) this.sfxBus.gain.value = sfx;
    if (this.ambienceBus) this.ambienceBus.gain.value = ambience;
    if (this.musicBus) this.musicBus.gain.value = this.volumes.music;
  }

  /** Test hook: playback state without exposing the raw audio graph. */
  get debugState(): {
    context: AudioContextState | 'none';
    hums: number;
    music: boolean;
    muted: boolean;
    counts: Record<string, number>;
  } {
    return {
      context: this.context?.state ?? 'none',
      hums: this.sources.size,
      music: this.musicNodes.length > 0,
      muted: this.volumes.muted,
      counts: { ...this.counts },
    };
  }

  /** Update the listener from the camera so panning follows the view. */
  setListener(position: Vector3, forward: Vector3, up: Vector3): void {
    if (!this.context) return;
    this.listenerPosition.copy(position);
    this.listenerForward.copy(forward);
    this.listenerUp.copy(up);

    const listener = this.context.listener;
    const now = this.context.currentTime;
    if (listener.positionX) {
      listener.positionX.setTargetAtTime(position.x, now, 0.02);
      listener.positionY.setTargetAtTime(position.y, now, 0.02);
      listener.positionZ.setTargetAtTime(position.z, now, 0.02);
      listener.forwardX.setTargetAtTime(forward.x, now, 0.02);
      listener.forwardY.setTargetAtTime(forward.y, now, 0.02);
      listener.forwardZ.setTargetAtTime(forward.z, now, 0.02);
      listener.upX.setTargetAtTime(up.x, now, 0.02);
      listener.upY.setTargetAtTime(up.y, now, 0.02);
      listener.upZ.setTargetAtTime(up.z, now, 0.02);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (listener as any).setPosition(position.x, position.y, position.z);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (listener as any).setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }
  }

  /** A short positional tone; used for distant threats and impacts. */
  playAt(name: SoundName, position: Vector3, gain = 1): void {
    if (!this.context || !this.sfxBus) return;
    this.counts[name] = (this.counts[name] ?? 0) + 1;
    const panner = this.context.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 2.5;
    panner.maxDistance = 60;
    panner.rolloffFactor = 1.1;

    if (panner.positionX) {
      panner.positionX.value = position.x;
      panner.positionY.value = position.y;
      panner.positionZ.value = position.z;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (panner as any).setPosition(position.x, position.y, position.z);
    }

    const nodeGain = this.context.createGain();
    nodeGain.gain.value = gain;
    panner.connect(nodeGain);
    nodeGain.connect(this.sfxBus);

    this.synthesise(name, panner, nodeGain);

    window.setTimeout(() => {
      nodeGain.disconnect();
      panner.disconnect();
    }, 2000);
  }

  /** A non positional sound: UI, the player's own weapon, ambience. */
  play(name: SoundName, gain = 1): void {
    if (!this.context || !this.sfxBus) return;
    this.counts[name] = (this.counts[name] ?? 0) + 1;
    const nodeGain = this.context.createGain();
    nodeGain.gain.value = gain;
    nodeGain.connect(this.sfxBus);
    this.synthesise(name, nodeGain, nodeGain);
    window.setTimeout(() => nodeGain.disconnect(), 2000);
  }

  private synthesise(name: SoundName, output: AudioNode, envelope: GainNode): void {
    if (!this.context) return;
    const now = this.context.currentTime;

    switch (name) {
      case 'shot': {
        const osc = this.context.createOscillator();
        osc.type = 'square';
        osc.frequency.setValueAtTime(880, now);
        osc.frequency.exponentialRampToValueAtTime(160, now + 0.12);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.5, now + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.16);
        break;
      }
      case 'droneShot': {
        const osc = this.context.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(360, now);
        osc.frequency.exponentialRampToValueAtTime(80, now + 0.18);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.38, now + 0.008);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.22);
        break;
      }
      case 'hit':
      case 'explosion': {
        const buffer = this.context.createBuffer(
          1,
          Math.floor(this.context.sampleRate * 0.35),
          this.context.sampleRate,
        );
        const data = buffer.getChannelData(0);
        for (let index = 0; index < data.length; index += 1) {
          const decay = 1 - index / data.length;
          data[index] = (Math.random() * 2 - 1) * decay * decay;
        }
        const source = this.context.createBufferSource();
        source.buffer = buffer;
        const filter = this.context.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(name === 'hit' ? 2600 : 900, now);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.6, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        source.connect(filter);
        filter.connect(gain);
        gain.connect(output);
        source.start(now);
        break;
      }
      case 'footstep':
      case 'magnet': {
        const osc = this.context.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(name === 'magnet' ? 420 : 240, now);
        osc.frequency.exponentialRampToValueAtTime(120, now + 0.09);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.28, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.12);
        break;
      }
      case 'jump': {
        const osc = this.context.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(180, now);
        osc.frequency.exponentialRampToValueAtTime(520, now + 0.18);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.3, now + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.22);
        break;
      }
      case 'land': {
        const osc = this.context.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(140, now);
        osc.frequency.exponentialRampToValueAtTime(60, now + 0.22);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.5, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.32);
        break;
      }
      case 'alarm': {
        const osc = this.context.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.setValueAtTime(330, now + 0.16);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.32);
        break;
      }
      case 'droneHum': {
        const osc = this.context.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.value = 90;
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.22, now + 0.08);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.55);
        break;
      }
      case 'ui':
      default: {
        const osc = this.context.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(620, now);
        osc.frequency.exponentialRampToValueAtTime(880, now + 0.07);
        const gain = this.context.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.22, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
        osc.connect(gain);
        gain.connect(output);
        osc.start(now);
        osc.stop(now + 0.12);
        break;
      }
    }

    void envelope;
  }

  /** A slow synthesised pad; a placeholder for the final score. */
  startMusic(): void {
    if (!this.context || !this.musicBus || this.musicNodes.length > 0) return;
    const now = this.context.currentTime;

    const filter = this.context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 260;
    filter.Q.value = 0.7;

    const gain = this.context.createGain();
    gain.gain.value = 0.25;
    filter.connect(gain);
    gain.connect(this.musicBus);

    const oscillators: OscillatorNode[] = [];
    const chord = [55, 65.41, 82.41, 110];
    chord.forEach((frequency, index) => {
      const osc = this.context?.createOscillator();
      if (!osc) return;
      osc.type = index % 2 === 0 ? 'sine' : 'triangle';
      osc.frequency.value = frequency;
      osc.detune.value = index * 4;
      osc.connect(filter);
      osc.start(now);
      oscillators.push(osc);
    });

    const lfo = this.context.createOscillator();
    lfo.frequency.value = 0.05;
    const lfoGain = this.context.createGain();
    lfoGain.gain.value = 0.07;
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);
    lfo.start(now);
    oscillators.push(lfo);

    this.musicNodes = oscillators;
    this.musicGain = gain;
  }

  stopMusic(): void {
    for (const node of this.musicNodes) {
      try {
        node.stop();
      } catch {
        // Already stopped.
      }
      node.disconnect();
    }
    this.musicNodes = [];
    this.musicGain?.disconnect();
    this.musicGain = null;
  }

  /** A quiet looping drone bed that follows the chamber. */
  startAmbience(): void {
    if (!this.context || !this.ambienceBus || this.ambience) return;

    const osc = this.context.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 58;
    const gain = this.context.createGain();
    gain.gain.value = 0.25;
    osc.connect(gain);
    gain.connect(this.ambienceBus);
    osc.start();
    this.ambience = osc;
    this.ambienceGain = gain;
  }

  stopAmbience(): void {
    if (!this.ambience) return;
    try {
      this.ambience.stop();
    } catch {
      // Already stopped.
    }
    this.ambience.disconnect();
    this.ambience = null;
    this.ambienceGain?.disconnect();
    this.ambienceGain = null;
  }

  /**
   * Positional hum for a drone. Each live drone owns a looping pair of
   * oscillators, so the player can hear it approach across the chamber.
   */
  updateDroneHum(id: string, position: Vector3, active: boolean): void {
    if (!this.context || !this.sfxBus) return;
    const now = this.context.currentTime;
    let source = this.sources.get(id);
    if (!active) {
      if (source) source.gain.gain.setTargetAtTime(0, now, 0.1);
      return;
    }
    if (!source) {
      const panner = this.context.createPanner();
      panner.panningModel = 'HRTF';
      panner.distanceModel = 'inverse';
      panner.refDistance = 3;
      panner.maxDistance = 50;
      panner.rolloffFactor = 1;

      const filter = this.context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 420;
      filter.Q.value = 2;

      const gain = this.context.createGain();
      gain.gain.value = 0;
      filter.connect(gain);
      gain.connect(panner);
      panner.connect(this.sfxBus);

      // Detuned oscillators give the hum a mechanical beat.
      const base = 70 + (hashString(id) % 5) * 5;
      const saw = this.context.createOscillator();
      saw.type = 'sawtooth';
      saw.frequency.value = base;
      const sub = this.context.createOscillator();
      sub.type = 'sine';
      sub.frequency.value = base * 0.5;
      saw.connect(filter);
      sub.connect(filter);
      saw.start(now);
      sub.start(now);

      source = { panner, gain, oscillators: [saw, sub] };
      this.sources.set(id, source);
    }
    if (source.panner.positionX) {
      source.panner.positionX.setTargetAtTime(position.x, now, 0.05);
      source.panner.positionY.setTargetAtTime(position.y, now, 0.05);
      source.panner.positionZ.setTargetAtTime(position.z, now, 0.05);
    }
    source.gain.gain.setTargetAtTime(0.16, now, 0.2);
  }

  /** Fade and release a destroyed drone's hum. */
  stopDroneHum(id: string): void {
    const source = this.sources.get(id);
    if (!source || !this.context) return;
    this.sources.delete(id);
    const now = this.context.currentTime;
    source.gain.gain.cancelScheduledValues(now);
    source.gain.gain.setTargetAtTime(0, now, 0.06);
    for (const oscillator of source.oscillators) {
      oscillator.stop(now + 0.4);
    }
    window.setTimeout(() => {
      for (const oscillator of source.oscillators) oscillator.disconnect();
      source.gain.disconnect();
      source.panner.disconnect();
    }, 600);
  }

  stopAllDroneHums(): void {
    for (const id of [...this.sources.keys()]) this.stopDroneHum(id);
  }
}

function hashString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}
